import React, {useEffect, useState} from "react";
import TableSearch from "../../../../../base/components/Table/tableSearch";
import Button from "../../../../../base/components/Button";
import {BUTTON_COLORS} from "../../../../../base/components/Button/appearance";
import {useLocationQuery} from "../../../../../base/hooks/useQueryString";
import {Dropdown, DropdownMenu, DropdownToggle, UncontrolledTooltip} from "reactstrap";
import {useHighlight} from "../../../../../base/hooks/useHighlight";
import {
    ADMINS_MANAGEMENT_STATUSES,
    ADMINS_MANAGEMENT_STATUSES_NAMES,
} from "../../../../../base/constants/shared";
import joinClassNames from "../../../../../base/helpers/joinClassNames";
import Icon from "../../../../../base/components/Icon";
import {useService} from "../../../../../base/hooks/useService";
import AdminsService from "../../../../../services/AdminsService";
import {KEY_USER} from "../../../../../base/constants/storage";
import StorageService from "../../../../../services/StorageService";
import {getArray} from "../../../HealthProgram/helpers";
import Checkbox from "../../../../../base/components/Checkbox";
import {isServiceEditable} from "../../../../../base/helpers/adminRolePermissions";

export const ADMINS_ACTIONS = {
    DELETE: 'delete',
    RESEND_INVITE: 'resendInvite',
    CHANGE_STATUS: 'changeStatus',
    EDIT: 'edit'
};

export const SearchPlaceholder = () => (
    <>
        No admins found
    </>
);

export const NoAdminsPlaceholder = () => (
    <div className="text-center">
        No admins for now.<br/>
        Click the “Invite admin” option to add a new one.
    </div>
);

const statusesPillClasses = {
    [ADMINS_MANAGEMENT_STATUSES.active]: 'active-pill',
    [ADMINS_MANAGEMENT_STATUSES.invited]:  'deactivated-pill',
    [ADMINS_MANAGEMENT_STATUSES.blocked]: 'blocked-pill'
}

const changeStatusNames = {
    [ADMINS_MANAGEMENT_STATUSES.active]: 'Block',
    [ADMINS_MANAGEMENT_STATUSES.blocked]: 'Unblock'
}

const changeStatusIcon = {
    [ADMINS_MANAGEMENT_STATUSES.active]: 'lock',
    [ADMINS_MANAGEMENT_STATUSES.blocked]: 'unlock'
}

const changeStatusValue = {
    [ADMINS_MANAGEMENT_STATUSES.active]: ADMINS_MANAGEMENT_STATUSES.blocked,
    [ADMINS_MANAGEMENT_STATUSES.blocked]: ADMINS_MANAGEMENT_STATUSES.active
}

const adminStatusesOption = Object.values(ADMINS_MANAGEMENT_STATUSES).map((value) => ({
    id: value,
    value,
    label: ADMINS_MANAGEMENT_STATUSES_NAMES[value]
}));

const TableFilter = ({ filterProvider, rolesOptions }) => {
    const {statuses, adminRoleIds} = filterProvider;

    const [isOpen, updateIsOpen] = useState(false);
    const [roles, updateRolesIds] = useState(getArray(adminRoleIds.getValue()));
    const [statusFilter, updateStatusFilter] = useState(getArray(statuses.getValue()));

    const submitFilters = () => {
        adminRoleIds.setValue(roles);
        statuses.setValue(statusFilter);
        updateIsOpen(false);
    }

    const handleCheckboxChange = (id, setState) => {
        setState(prevState => {
            return prevState.includes(id) ? prevState.filter(item => item !== id) : [...prevState, id];
        });
    }

    return (
        <section className="d-flex align-items-center">
            <Dropdown
                isOpen={isOpen}
                toggle={() => updateIsOpen(prevState => !prevState)}
                className="d-inline-block"
            >
                <DropdownToggle
                    className=" btn btn-outline-primary d-flex align-items-center"
                    tag="button"
                >
                    <i className="bx bx-filter-alt me-2"/> Add filter
                </DropdownToggle>

                <DropdownMenu className="filter-menu p-2 pt-3 rec-filter">
                    <section className="d-flex justify-content-between">
                        <section className="filter-options mx-1 me-4 custom-scrollbar rec-category-filter">
                            <label className="text-secondary font-size-10 mb-1 font-weight-normal">BY ROLE</label>
                            {rolesOptions.map((role, index) => {
                                return (
                                    <Checkbox
                                        id={role.id}
                                        text={role.label}
                                        value={roles?.includes(role.id)}
                                        onChange={() => {
                                            handleCheckboxChange(role.id, updateRolesIds)
                                        }}
                                        key={index}
                                        className="my-2 ms-1"
                                    />
                                )
                            })}
                        </section>

                        <section className="filter-options mx-1 custom-scrollbar rec-activity-filter">
                            <label className="text-secondary font-size-10 mb-1 font-weight-normal">BY STATUS</label>
                            {adminStatusesOption.map((status, index) => {
                                return (
                                    <Checkbox
                                        id={status.label}
                                        text={status.label}
                                        value={statusFilter?.includes(status.id)}
                                        onChange={() => handleCheckboxChange(status.id, updateStatusFilter)}
                                        key={index}
                                        className="my-2 ms-1"
                                    />
                                )
                            })}
                        </section>
                    </section>

                    <section className="w-100 px-2 pb-2 mt-3">
                        <Button color={BUTTON_COLORS.primary} onClick={submitFilters} className="w-100">
                            Apply
                        </Button>
                    </section>
                </DropdownMenu>
            </Dropdown>

            {(!!statuses.getValue() || !!adminRoleIds.getValue()) &&
            <Button
                color={BUTTON_COLORS.transparent}
                onClick={() => {
                    updateRolesIds([]);
                    updateStatusFilter([])
                    statuses.setValue();
                    adminRoleIds.setValue();
                }}
                className="text-danger no-border ms-2"
            >
                Clear filter
            </Button>
            }
        </section>
    )
}

export const TableHeader = ({searchProvider, filterProvider, headerActions}) => {
    const [roles, setRoles] = useState([])

    const goToInvite = () => {
        headerActions.onClickInvite()
    }

    const adminsService = useService(AdminsService)
    const storage = useService(StorageService);

    const DEFAULT_OPTIMALLY_ME_ORG = 1;
    const {organisationId} = storage.get(KEY_USER, {organisationId: DEFAULT_OPTIMALLY_ME_ORG});

    const mapRolesToDropdown = (data = []) => {
        return data.map((item) => ({ id: item.id, label: item.name }));
    };

    const getRoles = () => {
        adminsService.getAdminsRoles(organisationId).then(({data}) =>
            setRoles(mapRolesToDropdown(data))
        )
    }

    useEffect(() => {
        getRoles()
    }, []);
    const isAdminCustomisationEditable = isServiceEditable("Admin UI Customisation");

    return (
        <section className="d-flex w-100 pt-1 mb-3 align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-4">
                <TableSearch
                    className="biomarkers-search"
                    search={searchProvider?.getValue()}
                    onSearch={searchProvider.setValue}
                    placeholder="Search admin..."
                />
            </div>

                <div className="d-flex gap-4">
                    <TableFilter filterProvider={filterProvider} rolesOptions={roles} />
                    {isAdminCustomisationEditable &&
                        <Button color={BUTTON_COLORS.primary} onClick={goToInvite} className="d-flex align-items-center">
                            <i className="bx bx-plus me-2"/>
                            Invite admin
                        </Button>
                    }
                </div>
        </section>
    );
};

export const columns = [
    {
        Header: "#",
        width: 35,
        className: "bg-white",
        Cell: (cellProps) => {
            const {params: {offset = 0}} = useLocationQuery();
            return <label className="mb-0">{cellProps.row.index + 1 + (offset ?? 0)}</label>;
        }
    },
    {
        Header: "Name",
        accessor: "name",
        className: "bg-white",
        canSort: true,
        Cell: ({row: { original }}) => {
            const {params} = useLocationQuery();
            const {decorateText} = useHighlight(params.search);
            const fullName = `${original?.firstName} ${original?.lastName}`

            return (
                <section className="w-fit-content d-flex align-items-center">
                    <div {...decorateText(fullName)} className="text-truncate mb-0" style={{maxWidth: "33vw"}}/>
                </section>
            );
        }
    },
    {
        Header: 'Email',
        accessor: 'email',
        className: "bg-white",
        width: 220,
        canSort: true,
        Cell: ({ value }) => {
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            return <div className="table-cell-25 text-truncate" {...decorateText(value)} />;
        },
    },
    {
        Header: 'Role',
        accessor: 'role',
        className: "bg-white",
        canSort: true,
        Cell: ({row: {id: rowId,  original }}) => {
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            const permissionsNames = original?.adminRole?.permissions?.map((permission) => permission?.service?.name)

            return <div className="d-flex ">
                <div className="table-cell-25 text-truncate" {...decorateText(original?.adminRole?.name)} />
                {permissionsNames?.length && <Icon
                    id={`permissions-${rowId}`}
                    icon={"infoCircleOutline"}
                    width={16}
                    height={16}
                    className={"cursor-pointer ms-2"}
                />}
                {permissionsNames?.length && <UncontrolledTooltip
                    popperClassName={joinClassNames(
                        "tooltip-alternative-name"
                    )}
                    placement="right"
                    target={`permissions-${rowId}`}
                >
                    <div className="d-flex flex-column align-items-start">
                        {permissionsNames?.map((name, index) => <div
                            className={joinClassNames(
                                (index !== permissionsNames?.length - 1) && "mb-2",
                                "font-size-13"
                            )}
                            key={index}
                        >
                            {name}
                        </div>)}
                    </div>
                </UncontrolledTooltip>}
            </div>
        },
    },
    {
        Header: 'Status',
        accessor: 'status',
        className: "bg-white",
        canSort: true,
        Cell: ({value}) => {
            return (
                <div className={joinClassNames(statusesPillClasses[value], 'w-fit-content')}>
                    {`${ADMINS_MANAGEMENT_STATUSES_NAMES[value] ?? ''}`}
                </div>
            )
        },
    },
    {
        Header: "Actions",
        className: "bg-white",
        width: 100,
        Cell: ({
                   row: {
                       id: rowId,
                       original: {id, status, email, firstName, lastName, adminRole}
                   },
                   actions
               }) => {

            return <section
                className="d-flex gap-1 align-items-end justify-content-end"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                {status && changeStatusIcon[status] && changeStatusNames[status] && <Icon
                    id={`change-status-${changeStatusNames[status]}-${rowId}`}
                    icon={changeStatusIcon[status]}
                    width={20}
                    height={20}
                    onClick={(event) => {
                        event.stopPropagation();
                        const model = {
                            status: changeStatusValue[status]
                        }
                        actions[ADMINS_ACTIONS.CHANGE_STATUS](id, model);
                    }}
                    className="me-4 cursor-pointer"
                />}
                {status && changeStatusIcon[status] && changeStatusNames[status] && <UncontrolledTooltip
                    target={`change-status-${changeStatusNames[status]}-${rowId}`}
                    popperClassName={joinClassNames(
                        "tooltip-alternative-name"
                    )}
                    placement="bottom"
                    innerClassName="font-size-13"
                >
                    {changeStatusNames[status]} admin
                </UncontrolledTooltip>}

                {status === ADMINS_MANAGEMENT_STATUSES.invited && <Icon
                    id={`resend-invite-${rowId}`}
                    icon={"envelope"}
                    width={20}
                    height={20}
                    onClick={(event) => {
                        event.stopPropagation();
                        const model = {
                            email,
                            firstName,
                            lastName,
                            roleId: adminRole.id
                        }
                        actions[ADMINS_ACTIONS.RESEND_INVITE](model);
                    }}
                    className="me-4 cursor-pointer"
                />}
                {status === ADMINS_MANAGEMENT_STATUSES.invited && <UncontrolledTooltip
                    target={`resend-invite-${rowId}`}
                    popperClassName={joinClassNames(
                        "tooltip-alternative-name"
                    )}
                    placement="bottom"
                    innerClassName="font-size-13"
                >
                    Resend invite
                </UncontrolledTooltip>}
                <Icon
                    icon={"edit"}
                    width={20}
                    height={20}
                    onClick={(event) => {
                        event.stopPropagation();
                        const admin = {
                            id, email, firstName, lastName,
                            roleId: adminRole.id
                        }
                        actions[ADMINS_ACTIONS.EDIT](admin);
                    }}
                    className="me-4 cursor-pointer"
                />
                <Icon
                    icon={"trashIcon"}
                    width={20}
                    height={20}
                    onClick={(event) => {
                        event.stopPropagation();
                        actions[ADMINS_ACTIONS.DELETE](id);
                    }}
                    className="cursor-pointer"
                />
            </section>;
        }
    }
]

export function getFilteredColumns() {
  const isAdminCustomisationEditable = isServiceEditable("Admin UI Customisation");

  if (isAdminCustomisationEditable) {
    return columns;
  }
  return columns.filter(col => col.Header !== "Actions");
}